import React from 'react'
import { useTranslation } from 'react-i18next'
import { clsx } from '../../../utils/helper'
import { DELIVERY_TIME, ORDER_METHOD, PAYMENT_METHOD, VOUCHER_TYPE_PERCENT } from '../../../constants'
import { getDeliveryTime } from '../../../utils'
import { useSelector } from 'react-redux'
import { getListAddress } from '../../../selectors'
import { paymentMethodsSupport as paymentMethodsSupportSelector } from '../../../selectors/restaurant'

import Textarea from '../../form/Textarea'
import InputFill from '../../form/InputFill'
import InputSelect from '../../form/InputSelect'
import PaymentMethodGroup from '../../common/PaymentMethodGroup'
import Button from '../../elements/Button'
import IconCheck from '../../../assets/icons/IconCheck'
import IconSearch from '../../../assets/icons/IconSearch'
import FormatCurrency from '../../elements/FormatCurrency'

import CashGold from '../../../assets/images/cash-gold.svg'
import PaypalFull from '../../../assets/images/paypal-full.svg'
import PayEC from '../../../assets/icons/pay_ec_full.png'

import IconPaypalActive from '../../../assets/icons/IconPaypalActive'
import IconCashActive from '../../../assets/icons/IconCashActive'
import IconCashCard from '../../../assets/icons/IconCashCard'

const MethodBox = ({ value, currentValue, onChange, name, methodUI }) => (
    <div
        className={clsx(['method-box', value === currentValue && 'active'])}
        onClick={() =>
            value !== currentValue && onChange({ target: { value, name } })
        }
    >
        <div className="method-logo">
            <img src={methodUI.src} />
        </div>
        <div className="method-name">{methodUI.label}</div>
    </div>
)

const CustomerInfo = ({
    handleSubmit,
    form,
    handleInputChange,
    clearErrorField,
    errors,
    handleAddressBlur,
    onVerifyVoucher,
    voucherVerified,
    voucherVerifying,
    voucherData,
    voucherError,
}) => {
    const { t } = useTranslation()
    const { orderMethod } = useSelector(state => state.OrderReducer)
    const listAddress = useSelector(getListAddress)
    const paymentMethodsSupport = useSelector(paymentMethodsSupportSelector)

    const listDeliveryTimes = getDeliveryTime()

    const groupsField = [
        {
            title: orderMethod === ORDER_METHOD.DELIVERY ? t('customerInfo.address') : t('customerInfo.personalInformation'),
            list: [
                listAddress?.length && {
                    label: 'customerInfo.lbSelectAddress',
                    name: 'selectAddress',
                    component: InputSelect,
                    props: {
                        options: [
                            <option key="opt-default" value={-1}>
                                {t('customerInfo.lbDefaultSelectAddress')}
                            </option>,
                            ...listAddress.map((address, index) => (
                                <option key={index} value={index}>
                                    {[
                                        `${address.street} ${address.addressNumber}`,
                                        `${address.zipCode} ${address.city}`,
                                        address.floor,
                                    ]
                                        .filter(Boolean)
                                        .join(', ')}
                                </option>
                            )),
                        ],
                    },
                },
                listAddress?.length && {
                    empty: true,
                },
                {
                    label: 'customerInfo.street',
                    name: 'userStreet',
                    props: {
                        onBlur: () => handleAddressBlur(),
                        required: true,
                    },
                },
                {
                    label: 'customerInfo.apartmentNumber',
                    name: 'userAddressNumber',
                    props: {
                        onBlur: () => handleAddressBlur(),
                        required: true,
                    },
                },
                {
                    label: 'customerInfo.postCode',
                    name: 'userPostcode',
                    props: {
                        onBlur: () => handleAddressBlur(),
                        required: true,
                    },
                },
                {
                    label: 'customerInfo.city',
                    name: 'userCity',
                    props: {
                        onBlur: () => handleAddressBlur(),
                        required: true,
                    },
                },
                {
                    label: 'customerInfo.floor',
                    name: 'floor',
                    props: {},
                },
            ],
        },
        {
            title: t('customerInfo.contactInfo'),
            list: [
                {
                    label: 'cartDesktop.lbCustomerName',
                    name: 'userName',
                    props: {
                        required: true,
                    },
                },
                {
                    label: 'customerInfo.phone',
                    name: 'userPhone',
                    props: {
                        numberOnly: true,
                        required: true,
                    },
                },
                {
                    label: 'customerInfo.office',
                    name: 'userOfficeName',
                    props: {},
                },
                {
                    label: 'customerInfo.email',
                    name: 'userEmail',
                    props: {},
                },
            ],
        },
        {
            title: t('cartDesktop.lbDeliverInfo'),
            list: [
                {
                    label:
                        orderMethod === ORDER_METHOD.DELIVERY
                            ? 'customerInfo.deliveryTime'
                            : 'customerInfo.pickupTime',
                    name: 'deliveryTime',
                    component: InputSelect,
                    props: {
                        options: listDeliveryTimes.map(time => (
                            <option value={time}>
                                {time === DELIVERY_TIME
                                    ? t('cartDesktop.lbSelectDeliverTime')
                                    : time}
                            </option>
                        )),
                    },
                },
                {
                    label: 'cartDesktop.lbNote',
                    name: 'userAddressNote',
                    component: Textarea,
                    props: {
                        rows: 4,
                    },
                },
            ],
        },
    ]

    return (
        <form onSubmit={handleSubmit}>
            {groupsField.map((group, indexGroup) => (
                <div key={indexGroup} className="food-desk-form-group">
                    <div className="title">{group.title}</div>
                    <div className="box-form">
                        <div className="row">
                            {group.list.filter(Boolean).map((field, index) => {
                                if (field.empty) {
                                    return (
                                        <div
                                            key={index}
                                            className="col-12 col-md-6"
                                        />
                                    )
                                }

                                const Component = field.component || InputFill
                                return (
                                    <div
                                        key={index}
                                        className="col-12 col-md-6"
                                    >
                                        <Component
                                            id={`cusInfo-${field.name}`}
                                            className="mt-6"
                                            label={t(field.label)}
                                            name={field.name}
                                            value={form[field.name] ?? ''}
                                            error={errors[field.name]}
                                            onChange={handleInputChange}
                                            onFocus={() =>
                                                clearErrorField(field.name)
                                            }
                                            inputStyle={{
                                                borderColor: '#CDCDCD',
                                            }}
                                            {...field.props}
                                        />
                                    </div>
                                )
                            })}
                        </div>
                    </div>
                </div>
            ))}
            <div className="row">
                <div className="col-md-6">
                    {!!paymentMethodsSupport.length && (
                        <div className="food-desk-form-group">
                            <div className="title">{t('cartDesktop.lbPaymentMethod')}</div>
                            <PaymentMethodGroup
                                onChange={handleInputChange}
                                value={form.paymentMethod}
                                name="paymentMethod"
                                list={[
                                    {
                                        label: t('customerInfo.paymentMethodGroupCash'),
                                        options: [
                                            paymentMethodsSupport.includes(PAYMENT_METHOD.CASH) && {
                                                widthIcon: true,
                                                label: t('customerInfo.cash'),
                                                value: PAYMENT_METHOD.CASH,
                                                icon: IconCashActive,
                                                unCheckClass: 'payment-type-uncheck',
                                            },
                                            paymentMethodsSupport.includes(PAYMENT_METHOD.CASH_CARD) && {
                                                widthIcon: true,
                                                label: t('customerInfo.cash_card'),
                                                value: PAYMENT_METHOD.CASH_CARD,
                                                icon: IconCashCard,
                                                unCheckClass: 'payment-type-uncheck',
                                            },
                                        ].filter(Boolean)
                                    },
                                    {
                                        label: t('customerInfo.paymentMethodGroupOnline'),
                                        options: [
                                            paymentMethodsSupport.includes(PAYMENT_METHOD.PAYPAL) && {
                                                widthIcon: true,
                                                label: 'Paypal',
                                                value: PAYMENT_METHOD.PAYPAL,
                                                icon: IconPaypalActive,
                                                unCheckClass: 'payment-type-uncheck',
                                            },
                                        ].filter(Boolean)
                                    },
                                ].filter(g => !!g.options.length)}
                            />
                        </div>
                    )}
                </div>
                <div className="col-md-6">
                    <div className="food-desk-form-group">
                        <div className="title">{t('voucher.discountCode')}</div>
                        <div className="box-form">
                            <div className="d-flex align-items-end">
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <InputFill
                                        id="cusInfo-discountCode"
                                        className="mt-6"
                                        label={t('voucher.discountCode')}
                                        name="discountCode"
                                        value={form.discountCode ?? ''}
                                        onChange={handleInputChange}
                                        onFocus={() => clearErrorField('discountCode')}
                                        inputStyle={{
                                            borderColor: voucherError ? '#FF0000' : '#CDCDCD',
                                        }}
                                    />
                                </div>
                                <div style={{ flexShrink: 0 }}>
                                    {voucherVerified ? (
                                        <Button outline disabled large style={{ padding: '4px 12px 0' }}>
                                            <IconCheck width={20} height={20} fill="green" />
                                        </Button>
                                    ) : (
                                        <Button
                                            onClick={onVerifyVoucher}
                                            disabled={voucherVerifying || !form.discountCode?.trim()}
                                            large
                                            style={{ padding: '4px 12px 0' }}
                                        >
                                            <IconSearch width={20} height={20} fill="#fff" />
                                        </Button>
                                    )}
                                </div>
                            </div>
                            {voucherError && (
                                <div className="voucher-error">{voucherError}</div>
                            )}
                            {voucherData && (
                                <div className="voucher-details">
                                    <div className="voucher-details__row">
                                        <span className="voucher-details__name">{voucherData.code}</span>
                                        <span className="voucher-details__value">
                                            {voucherData.type === VOUCHER_TYPE_PERCENT
                                                ? `${voucherData.value}%`
                                                : <FormatCurrency amount={voucherData.value} />}
                                        </span>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </form>
    )
}

export default CustomerInfo
